// node_modules
import { FC, useCallback, useContext, useEffect, useState } from "react";
// import ContentLoader from "react-content-loader";
// Components
import { AsynchronousWrapper, ChatContainer, DivDropdown } from "Components";
import { Answers } from "./Answers";
// Constants
import { GeneralConstants, LoggingConstants, TechnologyConstants } from "Constants";
// Contexts
import { WebsocketContext } from "Providers";
import { AuthContext, CaseContext } from "Stores";
// Types
import { TAnswerResultDTO, TChatMessage, TConcept, TFindAnswersOnTechnologyDTO, TTechnology, TUseFetch } from "Types";
// Helpers
import { CaseActionTypeEnum, ToastTypeEnum } from "Enums";
import { PendoHelperSingleton, ToastHelperSingleton } from "Helpers";
import { useFetch } from "Hooks";
// Styles
import { ICaseSelectedTechnology } from "Interfaces";
import savedQueriesStyles from "../SavedQueries/savedQueries.module.scss";
import styles from "./findAnswers.module.scss";

// const ChatContainerLoader = () => (
//     <ContentLoader
//         animate={true}
//         height="15"
//         width="100%"
//         speed={2}
//         backgroundColor="#fcfcfc"
//         foregroundColor="#ecebeb"
//     >
//         <rect x="32" y="0" rx="5" ry="5" width="70%" height="15" /> 
//     </ContentLoader>
// );

export const FindAnswers: FC = () => {
    // Contexts
    const { webSocketController } = useContext(WebsocketContext);
    const { caseState, caseDispatch } = useContext(CaseContext);
    const { authState } = useContext(AuthContext);

    // State
    const [technologyList, setTechnologyList] = useState<TTechnology[]>([]);
    const [selectedTechnology, setSelectedTechnology] = useState<TTechnology | undefined>(undefined);
    const [selectedTechnologyIndex, setSelectedTechnologyIndex] = useState<number>(0);
    const [messages, setMessages] = useState<TChatMessage[]>([]);
    const [igorIsTyping, setIgorIsTyping] = useState<boolean>(false);
    const [lastAskedQuestion, setLastAskedQuestion] = useState<string>("");

    // Custom hooks
    const { isLoading: isConceptListLoading, didError: didConceptListError, errorMessage: conceptListErrorMessage, fetchedData: fetchedConceptList }: TUseFetch<TConcept[]> = useFetch(`api/cases/${caseState.selectedCaseId}/concepts?doIncludeAdditional=true`);

    // Logic
    const getEmptyQuestionMessage = useCallback((): TChatMessage => {
        return {
            content: "",
            isResponse: false,
            isEditable: true,
            username: authState.userEmail
        };
    }, [authState.userEmail]);

    const functionalAddMessage = useCallback((newMessage: TChatMessage) => {
        setMessages(currentMessages => {
            const newMessages: TChatMessage[] = [...currentMessages];
            newMessages.push(newMessage);
            return [...newMessages];
        });
    }, []);

    const functionalInitMessages = useCallback((newMessages: TChatMessage[]) => {
        setMessages(currentMessages => {
            return [...newMessages];
        });
    }, []);

    const functionalAddEmptyMessage = useCallback(() => {
        setMessages(currentMessages => {
            if (currentMessages.length > 0 && currentMessages[currentMessages.length - 1].content !== "") {
                const newMessages: TChatMessage[] = [...currentMessages];
                newMessages.push(getEmptyQuestionMessage());
                return [...newMessages];
            } else {
                return [...currentMessages];
            }
        });
    }, [getEmptyQuestionMessage]);

    const functionalUpdateMessage = useCallback((content: string, isEditable: boolean, messageIndex: number) => {
        setMessages(currentMessages => {
            const newMessages: TChatMessage[] = [...currentMessages];
            for (let index = 0; index < newMessages.length; index++) {
                if (index === messageIndex) {
                    const currentMessage = newMessages[index];
                    currentMessage.content = content;
                    currentMessage.isEditable = isEditable;
                }
            }
            return [...newMessages];
        });
    }, []);

    useEffect(() => {
        if (!fetchedConceptList || fetchedConceptList.length <= 0) {
            setTechnologyList([]);
            setSelectedTechnologyIndex(0);
        } else {
            const newTechnologyList: TTechnology[] = [];
            
            const generalTechnology: TTechnology = {
                id: TechnologyConstants.GENERAL_TECHNOLOGY_ID,
                originalTechnologyId: TechnologyConstants.GENERAL_TECHNOLOGY_ID,
                name: TechnologyConstants.GENERAL_TECHNOLOGY_NAME,
                isUserAdded: true,
                sideNotes: [],
                queryCount: 0
            };
            newTechnologyList.push(generalTechnology);
            
            for (const concept of fetchedConceptList) {
                newTechnologyList.push(...concept.technologies);
            }

            setTechnologyList(newTechnologyList);
        }
    }, [fetchedConceptList]);

    const onTechnologySelected = useCallback((newlySelectedTechnology: TTechnology) => {
        setSelectedTechnology(newlySelectedTechnology);
        setIgorIsTyping(false);
        functionalInitMessages([getEmptyQuestionMessage()]);
    }, [functionalInitMessages, getEmptyQuestionMessage]);

    useEffect(() => {
        if (selectedTechnology === undefined) {
            let newSelectedTechnology: TTechnology | undefined = undefined;
            for (let index = 0; index < technologyList.length; index++) {
                const technology = technologyList[index];
                if ((!technology.isUserAdded && technology.id === caseState.selectedOriginalTechnologyId) || 
                        (technology.isUserAdded && technology.id === caseState.selectedTechnologyId)) {
                    newSelectedTechnology = technology;
                    setSelectedTechnologyIndex(index);
                    setSelectedTechnology(technology);
                    break;
                }
            }
            if (!newSelectedTechnology && technologyList && technologyList.length > 0) {
                setSelectedTechnologyIndex(0);
                setSelectedTechnology(technologyList[0]);
            }
        }
    }, [caseState.selectedOriginalTechnologyId, caseState.selectedTechnologyId, selectedTechnology, technologyList]);
    
    useEffect(() => {
        functionalInitMessages([getEmptyQuestionMessage()]);
    }, [functionalInitMessages, getEmptyQuestionMessage]);

    const dealWithNewAnswerResults = useCallback((newAnswerResults: TAnswerResultDTO[]): void => {
        const newMessage: TChatMessage = {
            content: <Answers
                question={lastAskedQuestion}
                answerResults={newAnswerResults}
                answerResultsPreviewCount={1} />,
            isResponse: true,
            isEditable: false,
            username: GeneralConstants.IGORAI_USERNAME
        };
        functionalAddMessage(newMessage);
    }, [lastAskedQuestion, functionalAddMessage]);

    const onReceiveAnswerResult = useCallback((answerResult: TAnswerResultDTO) => {
        dealWithNewAnswerResults([answerResult]);
    }, [dealWithNewAnswerResults]);

    const onReceiveAnswerResults = useCallback((newAnswerResults: TAnswerResultDTO[]) => {
        dealWithNewAnswerResults(newAnswerResults);
    }, [dealWithNewAnswerResults]);

    const receiveEndProgress = useCallback(() => {
        setIgorIsTyping(false);
        functionalAddEmptyMessage();
    }, [functionalAddEmptyMessage]);

    const receiveSnackbar = (toastType: string, message: string) => {
        if(toastType.toLowerCase() === "error") {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, message);
            PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}ReceivedErrorSnackbar`, {
                error: message
            });
        } else if(toastType.toLowerCase() === "info") {
            ToastHelperSingleton.showToast(ToastTypeEnum.Notification, message);
            PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}ReceivedInfoSnackbar`, {
                error: message
            });
        }
    };

    const subscribeToResultFunctions = useCallback(async () => {
        await webSocketController.addHandler("ReceiveAnswerResult", onReceiveAnswerResult);
        await webSocketController.addHandler("ReceiveAnswerResults", onReceiveAnswerResults);
        await webSocketController.addHandler("ReceiveEndProgress", receiveEndProgress);
        await webSocketController.addHandler("ReceiveSnackbar", receiveSnackbar);
    }, [onReceiveAnswerResult, onReceiveAnswerResults, receiveEndProgress, webSocketController]);

    const unsubscribeToResultFunctions = useCallback(() => {
        webSocketController.removeHandler("ReceiveAnswerResult", onReceiveAnswerResult);
        webSocketController.removeHandler("ReceiveAnswerResults", onReceiveAnswerResults);
        webSocketController.removeHandler("ReceiveEndProgress", receiveEndProgress);
        webSocketController.removeHandler("ReceiveSnackbar", receiveSnackbar);
    }, [onReceiveAnswerResult, onReceiveAnswerResults, receiveEndProgress, webSocketController]);

    const onAskQuestion = (question: string, messageIndex: number) => {
        if (!caseState.selectedCaseId || !selectedTechnology || !question) { return; }
        setLastAskedQuestion(question);
        functionalUpdateMessage(question, false, messageIndex);

        const findAnswersOnTechnology: TFindAnswersOnTechnologyDTO = {
            caseId: caseState.selectedCaseId,
            question: question,
            technologyId: selectedTechnology.id
        };

        setIgorIsTyping(true);
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}AskQuestion`, {
            caseId: caseState.selectedCaseId,
            question: question,
            technologyId: selectedTechnology.id
        });
        webSocketController.invokeFunction("StartFindAnswersOnTechnology", findAnswersOnTechnology);
    };

    useEffect(() => {
        subscribeToResultFunctions();
        return () => unsubscribeToResultFunctions();
    }, [subscribeToResultFunctions, unsubscribeToResultFunctions]);

    useEffect(() => {
        if (!selectedTechnology) return;

        caseDispatch({ type: CaseActionTypeEnum.SetCaseSelectedTechnology, 
            selectedOriginalTechnologyId: selectedTechnology.id,
            selectedTechnologyId: undefined,
            selectedEntityName: "",
            selectedEntityNumbering: ""} as ICaseSelectedTechnology);
        
    }, [caseDispatch, selectedTechnology]);

    return (
        <AsynchronousWrapper didError={didConceptListError} errorMessage={conceptListErrorMessage} isLoading={false} LoaderComponent={null}>
            <div className={styles.userflowQuestionAnswering}>
                <div className={styles.resultSelectionContainer}>
                    <span>Select result</span>
                    {technologyList.length > 0 || isConceptListLoading ?
                            <DivDropdown objectList={technologyList} selectedObjectIndex={selectedTechnologyIndex}
                                newObjectSelected={onTechnologySelected}>
                                {technologyList.map((technology: TTechnology) => {
                                    return (
                                        <div key={technology.id} className={savedQueriesStyles.selectableTechnologyDropdownElement} title={technology.name}>
                                            <span>{technology.name}</span>
                                        </div>
                                    );
                                })}
                            </DivDropdown>
                        : 
                            <h5 style={{ marginLeft: "1rem" }}>You don&apos;t have any saved technologies.</h5>
                    }
                </div>
                {isConceptListLoading ?  <div></div> :
                    <ChatContainer
                        onSendChatMessage={onAskQuestion}
                        sendChatMessagePlaceholder="Ask your questions about the results here."
                        messages={messages}
                        isTyping={igorIsTyping}
                    />
                }
            </div>
        </AsynchronousWrapper>
    );
};